import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useHubspotForm } from '@aaronhayes/react-use-hubspot-form';
import { Helmet } from 'react-helmet';
import { useHotkeys } from 'react-hotkeys-hook';

import { useInputText } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { userSelectors } from '@/redux/selectors';
import { userActions } from '@/redux/actions';
import {
  GoogleAuthOneTapRequest,
  GoogleTokenTwoFARequest,
  SigninRequest,
} from '@/api';
import { Validation } from '@/models';
import { EMAIL_REGEX, ErrorCodeEnum, FALLBACK_PASSWORD_REGEX } from '@/config';

import parse from 'html-react-parser';
import {
  AlertBox,
  AuthContainer,
  Button,
  Input,
  SocialAuthButtons,
} from '@/components';

import styles from './SignIn.module.sass';
import { TwoFactorAuthModal } from '../TwoFactorAuthModal/TwoFactorAuthModal';

const PORTAL_ID = (import.meta.env.VITE_REACT_APP_HUBSPOT_PORTAL_ID as string)!;
const SIGNUP_FORM_ID = (import.meta.env
  .VITE_REACT_APP_HUBSPOT_SIGNUP_FORM_ID as string)!;

const GOOGLE_CLIENT_ID = (import.meta.env
  .VITE_REACT_APP_GOOGLE_CLIENT_ID as string)!;

interface SignInProps {}

const SignIn: FC<SignInProps> = () => {
  const { t } = useTranslation();
  const focusedInputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useAppDispatch();
  const { error, loading, errorCode, pendingGoogleToken } = useAppSelector(
    userSelectors.getUserSlice,
  );
  const isAuth = useAppSelector(userSelectors.getAuth);

  const formDivId = 'hubspot-form-wrapper';
  const [hubspotForm, setHubspotForm] = useState<HTMLFormElement | null>();
  const { formCreated } = useHubspotForm({
    portalId: PORTAL_ID,
    formId: SIGNUP_FORM_ID,
    target: `#${formDivId}`,
    onFormSubmitted: () => {
      console.log('submitted');
    },
    onFormReady: (form) => setHubspotForm(() => form),
  });

  const [isShow2FAModal, setIsShow2FAModal] = useState(false);

  const [googlePreservedData, setGooglePreservedData] =
    useState<boolean>(false);
  const twoFAInvalid = errorCode === ErrorCodeEnum.TWO_FA_ERROR;
  const twoFARequired = errorCode === ErrorCodeEnum.TWO_FA_REQUIRED;
  const showTwoFAInput = twoFAInvalid || twoFARequired;

  useEffect(() => {
    dispatch(userActions.setError(null));
  }, []);

  const [searchParams] = useSearchParams();

  const hsCallback = (email: string) => {
    try {
      if (hubspotForm) {
        const emailInput = hubspotForm.querySelector(
          'input[name="email"]',
        ) as HTMLInputElement;

        emailInput.value = email;

        hubspotForm.submit();
      }
    } catch (err) {
      console.log('HUBSPOT ERROR', err);
    }
  };

  useEffect(() => {
    if (isAuth || !window.google?.accounts) return;

    if (window.google?.accounts) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        context: 'signin',
        cancel_on_tap_outside: false,
        callback: (response: any) => {
          const { credential } = response;
          const payload: GoogleAuthOneTapRequest = {
            token: credential,
          };
          payload.hsCallback = hsCallback;
          dispatch(userActions.googleAuthOneTap(payload));
        },
        use_fedcm_for_prompt: true,
      });
      window.google.accounts.id.prompt();
    }

    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, [window.google]);

  useEffect(() => {
    if (showTwoFAInput || (error && twoFARequired)) {
      setIsShow2FAModal(true);
    }
  }, [showTwoFAInput, error, twoFARequired]);

  const EMAIL_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'email',
        value: EMAIL_REGEX,
        message: t('translation.invalid_email'),
      },
    ],
    [t],
  );

  const PASSWORD_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'regex',
        value: FALLBACK_PASSWORD_REGEX,
        message: t('translation.invalid_password'),
      },
    ],
    [t],
  );

  const TOKEN_VALIDATIONS: Validation[] = useMemo(() => [], []);

  const emailField = useInputText('', EMAIL_VALIDATIONS);
  const passwordField = useInputText('', PASSWORD_VALIDATIONS);
  const twoFACodeField = useInputText('', TOKEN_VALIDATIONS);

  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      const payload: GoogleTokenTwoFARequest = { code: codeResponse.code };
      payload.hsCallback = hsCallback;

      dispatch(userActions.googleAuth(payload));
      setGooglePreservedData(true);
    },
    onError: (errorResponse) => {
      userActions.setError(errorResponse.error || 'Something went wrong');
    },
  });

  const submitHandler = useCallback(() => {
    if (!loading) {
      if (googlePreservedData && pendingGoogleToken) {
        const payload: GoogleTokenTwoFARequest = {
          pendingToken: pendingGoogleToken,
          token2FA: twoFACodeField.value,
        };
        dispatch(userActions.googleAuth(payload));
      } else if (emailField.isValid && passwordField.isValid) {
        const payload: SigninRequest = {
          emailAddress: emailField.value,
          password: passwordField.value,
          token2fa: twoFACodeField.value,
        };

        dispatch(userActions.signin(payload));
      } else {
        if (!emailField.isValid) {
          emailField.onBlur();
        }
        if (!passwordField.isValid) {
          passwordField.onBlur();
        }
      }
    }
  }, [
    loading,
    googlePreservedData,
    pendingGoogleToken,
    emailField,
    passwordField,
    twoFACodeField.value,
    dispatch,
  ]);

  useHotkeys(
    'enter',
    (event) => {
      event.preventDefault();
      focusedInputRef.current = document.querySelector(
        'input:focus',
      ) as HTMLInputElement;
      if (focusedInputRef.current) {
        focusedInputRef.current.blur();
        submitHandler();
      }
    },
    { enableOnFormTags: true },
    [submitHandler],
  );

  useEffect(() => {
    if (!loading && focusedInputRef.current) {
      focusedInputRef.current.focus();
    }
  }, [loading]);

  if (isAuth) {
    return <Navigate to="/" />;
  }

  const signupLink = () => {
    const strSearchParams = searchParams.toString();
    let signupPath = '/signup';
    if (strSearchParams.length > 0) {
      signupPath += `?${strSearchParams}`;
    }

    return signupPath;
  };

  const on2FAClose = () => {
    setIsShow2FAModal(false);
    twoFACodeField.onChange('');
    dispatch(userActions.setErrorCode(null));
  };

  return (
    <>
      <Helmet>
        <title>AMLBot - {t('translation.sign_in')}</title>
        <meta
          name="description"
          content="Sign in to AMLBot web-interface for secure access to your cryptocurrency compliance tools. Monitor and manage your transactions with ease."
        />
      </Helmet>
      <AuthContainer
        title={t('translation.sign_in')}
        description={
          <>
            {t('translation.or')}{' '}
            <Link to={signupLink()} className="link">
              {t('translation.create_an_account')}
            </Link>
          </>
        }
        alert={
          !!error &&
          !showTwoFAInput && (
            <AlertBox type="danger">
              {{
                title: parse(t('translation.ooops')),
                description: error,
                icon: '🤔',
              }}
            </AlertBox>
          )
        }
        fields={
          <>
            <div id="hubspot-form-wrapper" style={{ height: 0, width: 0 }} />
            {!googlePreservedData && (
              <>
                <div>
                  <Input
                    id="email"
                    name="email"
                    type="text"
                    value={emailField.value}
                    onChange={emailField.onChange}
                    onBlur={emailField.onBlur}
                    isError={emailField.isDirty && !emailField.isValid}
                    placeholder={t('translation.email') || ''}
                    disabled={loading}
                    required
                    floating
                  />
                  {emailField.isDirty && !emailField.isValid && (
                    <AlertBox type="danger" small>
                      {{ description: emailField.errors[0] }}
                    </AlertBox>
                  )}
                </div>

                <div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={passwordField.value}
                    onChange={passwordField.onChange}
                    onBlur={passwordField.onBlur}
                    isError={passwordField.isDirty && !passwordField.isValid}
                    placeholder={t('translation.password') || ''}
                    disabled={loading}
                    required
                    floating
                  />
                  {passwordField.isDirty && !passwordField.isValid && (
                    <AlertBox type="danger" small>
                      {{ description: passwordField.errors[0] }}
                    </AlertBox>
                  )}
                </div>
              </>
            )}

            {/* enter_2fa_code_str */}
            {!googlePreservedData && (
              <div className={styles.forgot}>
                <Link to="/password/forgot" className="link">
                  {t('translation.forgot_your_password')}
                </Link>
              </div>
            )}
          </>
        }
        buttons={
          <>
            <Button
              size="xl"
              corner="rect"
              onClick={submitHandler}
              disabled={loading}
              fullwidth
            >
              {t('translation.sign_in')}
            </Button>

            {!googlePreservedData && (
              <SocialAuthButtons
                mode="signIn"
                onClickAuthGoogle={login}
                disabled={loading}
              />
            )}
          </>
        }
      />

      <TwoFactorAuthModal
        isOpen={isShow2FAModal}
        isLoading={loading}
        errorText={
          twoFAInvalid ? String(t('translation.2fa_token_error')) : undefined
        }
        onChange={twoFACodeField.onChange}
        onSubmit={submitHandler}
        onClose={on2FAClose}
      />
    </>
  );
};

export default SignIn;
